import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as invoicesApi from '../api/invoices';
import LoadingSpinner from '../components/LoadingSpinner';

function InvoiceListPage() {
    const { loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);

    // Calculate pagination
    const totalItems = invoices.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedInvoices = invoices.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo(0, 0);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    useEffect(() => {
        const getInvoices = async () => {
            if (authLoading) {
                setLoading(false);
                return;
            }

            try {
                setError(null);
                setSuccess(null);
                setLoading(true);

                const filters = {};
                if (filterStatus !== 'all') {
                    filters.status = filterStatus;
                }

                const response = await invoicesApi.fetchInvoices(filters);
                setInvoices(response.invoices || []);
            } catch (err) {
                console.error('Failed to fetch invoices:', err);
                // Handle the case where invoice tables don't exist yet
                if (err.message.includes('table') || err.message.includes('exist')) {
                    setError('Invoice system not yet set up. Please run the database setup script first.');
                } else {
                    setError(err.message || 'Failed to load invoices.');
                }
            } finally {
                setLoading(false);
            }
        };

        getInvoices();
    }, [authLoading, filterStatus]);

    const handleDelete = async (invoiceId) => {
        if (!window.confirm('Are you sure you want to permanently delete this invoice? This action cannot be undone.')) {
            return;
        }
        setLoading(true);
        try {
            setError(null);
            setSuccess(null);
            await invoicesApi.deleteInvoice(invoiceId);
            setSuccess('Invoice deleted successfully!');
            setInvoices(prevInvoices => prevInvoices.filter(invoice => invoice.id !== invoiceId));
        } catch (err) {
            console.error('Failed to delete invoice:', err);
            setError(err.message || 'Failed to delete invoice.');
        } finally {
            setLoading(false);
        }
    };

    const handleDuplicate = async (invoiceId) => {
        if (!window.confirm('Are you sure you want to duplicate this invoice?')) {
            return;
        }
        setLoading(true);
        try {
            setError(null);
            setSuccess(null);
            const response = await invoicesApi.duplicateInvoice(invoiceId);
            setSuccess('Invoice duplicated successfully!');
            navigate(`/invoices/edit/${response.invoice.id}`);
        } catch (err) {
            console.error('Failed to duplicate invoice:', err);
            setError(err.message || 'Failed to duplicate invoice.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'draft': return 'bg-gray-200 text-gray-800';
            case 'sent': return 'bg-blue-100 text-blue-800';
            case 'paid': return 'bg-green-100 text-green-800';
            case 'overdue': return 'bg-red-100 text-red-800';
            case 'partial': return 'bg-yellow-100 text-yellow-800';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const getPaymentStatus = (invoice) => {
        const totalPaid = parseFloat(invoice.total_paid || 0);
        const total = parseFloat(invoice.total || 0);
        
        if (totalPaid >= total && total > 0) return 'paid';
        if (totalPaid > 0) return 'partial';
        
        // Check if overdue
        const dueDate = new Date(invoice.due_date);
        const today = new Date();
        if (dueDate < today && invoice.status !== 'draft') return 'overdue';
        
        return invoice.status;
    };

    if (authLoading || loading) {
        return <LoadingSpinner message="Loading Invoices..." />;
    }

    if (error) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md max-w-md text-center" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <h2 className="text-3xl font-bold text-primary-700">Your Invoices</h2>
                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                        <Link
                            to="/invoices/new"
                            className="px-4 py-2 bg-primary-600 text-white font-medium rounded-lg shadow-md hover:bg-primary-700 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 text-center"
                        >
                            + Create New Invoice
                        </Link>
                    </div>
                </div>
                <div className="mb-6"></div>

                {success && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4" role="alert">
                        <span className="block sm:inline">{success}</span>
                    </div>
                )}

                {/* Filters and Controls */}
                <div className="mb-6 p-4 bg-gray-100 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <label htmlFor="filterStatus" className="text-sm font-medium text-gray-700">Status:</label>
                        <select
                            id="filterStatus"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="p-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm bg-white"
                        >
                            <option value="all">All</option>
                            <option value="draft">Draft</option>
                            <option value="sent">Sent</option>
                            <option value="partial">Partially Paid</option>
                            <option value="paid">Paid</option>
                            <option value="overdue">Overdue</option>
                        </select>
                    </div>
                </div>

                {invoices.length === 0 ? (
                    <p className="text-gray-600 text-center py-8 border border-dashed border-gray-300 rounded-lg">
                        {filterStatus === 'all' ? 'You haven\'t created any invoices yet.' : `No ${filterStatus} invoices found.`}
                        {filterStatus === 'all' && <Link to="/invoices/new" className="text-primary-600 underline mt-2 block">Click here to create your first invoice!</Link>}
                    </p>
                ) : (
                    <div>
                        {/* Desktop Table View */}
                        <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-blue-50">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                                            Invoice #
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                                            Client
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-blue-700 uppercase tracking-wider">
                                            Due Date
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-blue-700 uppercase tracking-wider">
                                            Total / Paid
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-blue-700 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {paginatedInvoices.map((invoice) => {
                                        const paymentStatus = getPaymentStatus(invoice);
                                        return (
                                            <tr key={invoice.id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">{invoice.invoice_number}</div>
                                                    <div className="text-xs text-gray-500">{invoice.title}</div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {invoice.client_name || 'Not Assigned'}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(paymentStatus)}`}>
                                                        {paymentStatus}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                                    {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'Not Set'}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                                                    <div className="font-semibold text-gray-900">
                                                        ${parseFloat(invoice.total || 0).toFixed(2)}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        Paid: ${parseFloat(invoice.total_paid || 0).toFixed(2)}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end space-x-2">
                                                        {invoice.public_access_token ? (
                                                            <Link
                                                                to={`/invoices/public/${invoice.public_access_token}`}
                                                                className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition duration-150 ease-in-out"
                                                                aria-label={`View ${invoice.invoice_number}`}
                                                                title="View Invoice"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                                                </svg>
                                                            </Link>
                                                        ) : (
                                                            <button
                                                                onClick={() => alert('Public access token not available. Please edit and save the invoice first.')}
                                                                className="text-gray-400 cursor-not-allowed p-1 rounded-full"
                                                                aria-label={`View ${invoice.invoice_number} (unavailable)`}
                                                                title="View Invoice (unavailable)"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                                                </svg>
                                                            </button>
                                                        )}
                                                        <Link
                                                            to={`/invoices/edit/${invoice.id}`}
                                                            className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                                                            aria-label={`Edit ${invoice.invoice_number}`}
                                                            title="Edit Invoice"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                                            </svg>
                                                        </Link>
                                                        <button
                                                            onClick={() => handleDuplicate(invoice.id)}
                                                            className="text-gray-600 hover:text-gray-900 p-1 rounded-full hover:bg-gray-100 transition duration-150 ease-in-out"
                                                            aria-label={`Duplicate ${invoice.invoice_number}`}
                                                            title="Duplicate Invoice"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(invoice.id)}
                                                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                                                            aria-label={`Delete ${invoice.invoice_number}`}
                                                            title="Delete Invoice"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="md:hidden space-y-4">
                            {paginatedInvoices.map((invoice) => {
                                const paymentStatus = getPaymentStatus(invoice);
                                return (
                                    <div key={invoice.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900">{invoice.invoice_number}</h3>
                                                <p className="text-sm text-gray-600">{invoice.title}</p>
                                            </div>
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(paymentStatus)}`}>
                                                {paymentStatus}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-1">
                                            <span className="font-medium">Client:</span> {invoice.client_name || 'Not Assigned'}
                                        </p>
                                        <p className="text-sm text-gray-600 mb-1">
                                            <span className="font-medium">Due:</span> {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'Not Set'}
                                        </p>
                                        <div className="text-sm mb-3">
                                            <p className="font-semibold text-gray-900">
                                                Total: ${parseFloat(invoice.total || 0).toFixed(2)}
                                            </p>
                                            <p className="text-gray-600">
                                                Paid: ${parseFloat(invoice.total_paid || 0).toFixed(2)}
                                            </p>
                                        </div>

                                        <div className="flex justify-end space-x-2 border-t border-gray-100 pt-3">
                                            {invoice.public_access_token ? (
                                                <Link
                                                    to={`/invoices/public/${invoice.public_access_token}`}
                                                    className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition duration-150 ease-in-out"
                                                    title="View Invoice"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                                    </svg>
                                                </Link>
                                            ) : (
                                                <button
                                                    onClick={() => alert('Public access token not available. Please edit and save the invoice first.')}
                                                    className="text-gray-400 cursor-not-allowed p-1 rounded-full"
                                                    title="View Invoice (unavailable)"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                                                    </svg>
                                                </button>
                                            )}
                                            <Link
                                                to={`/invoices/edit/${invoice.id}`}
                                                className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50 transition duration-150 ease-in-out"
                                                title="Edit Invoice"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                                </svg>
                                            </Link>
                                            <button
                                                onClick={() => handleDuplicate(invoice.id)}
                                                className="text-gray-600 hover:text-gray-900 p-1 rounded-full hover:bg-gray-100 transition duration-150 ease-in-out"
                                                title="Duplicate Invoice"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(invoice.id)}
                                                className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition duration-150 ease-in-out"
                                                title="Delete Invoice"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {invoices.length > 0 && (
                    <div className="mt-6 flex flex-col items-center space-y-4 sm:flex-row sm:justify-between sm:space-y-0">
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">Items per page:</span>
                            <select 
                                value={itemsPerPage} 
                                onChange={handleItemsPerPageChange}
                                className="p-2 border border-gray-300 rounded-md text-sm"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-600">
                                Page {currentPage} of {totalPages} ({startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} items)
                            </span>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default InvoiceListPage;